import React, { useRef, useState } from 'react';
import Box from '@ff/ui/Box';
import FullScreenModal from '@ff/ui/Modal/presets/FullScreenModal';
import FadeSlideShow from '@ff/ui/SlideShow/presets/FadeSlideShow';
import SimpleSlideShow from '@ff/ui/SlideShow';
import SectionNavigator from '@ff/ui/SectionNavigator';
import SlideContent from './SlideContent';
import SlideContentDetail from './SlideContentDetail';
import HighlightsDetail from './HighlightsDetail';
import type { Props as SlideContentProps } from './SlideContent';
import EditorSlot from './EditorSlot';
import ComponentLibrary from 'shared/widgets';
import FloatingCTA from './FloatingCTA';
import { zIndices } from './constants';
import { AppearDetector } from './AppearDetector';
import BackNavigation from 'shared/components/BackNavigation';
import ShareNavigation from './ShareNavigation';

import type { Props as PropsSchema } from './props-schema';

interface Props extends Omit<PropsSchema, 'slides'> {
  _id: string;
  slides?: SlideContentProps[];
  editor?: object;
  subcomponents?: object[];
  shareNavigation?: boolean;
}

export default function ProductSlideShow(props: Props) {
  const {
    slides,
    navigatorEnabled,
    alwaysShowDownArrow,
    showDownArrowLastSlide,
    editor,
    subcomponents,
    backNavigation,
    shareNavigation,
    slideScrollSnapAnimation,
    floatingCta
  } = props;

  const [navigatorAppear, setNavigatorAppear] = useState(true);
  const [activeModalId, setActiveModalId] = useState<string | null>(null);

  const fullScreenModalRef = useRef(null);

  const navLinks = slides?.map(s => ({ id: s.id, label: s.navigatorLabel, colorTheme: s.navigatorTheme }));
  const slideHighlights = slides?.find(element => element.id === activeModalId);

  const handleClick = (id: string | null) => {
    setActiveModalId(id)
  }

  const isEditor = !!editor;

  let SlideShow = FadeSlideShow;

  if (isEditor || !slideScrollSnapAnimation) {
    SlideShow = SimpleSlideShow;
  }

  const shouldShowDownArrow = (isLastSlide: boolean) => {
    return !isLastSlide || showDownArrowLastSlide;
  }

  // Get share content from the first slide for the share navigation
  const shareContent = slides && slides.length > 0 ? {
    url: window.location.href,
    title: slides[0].title || document.title,
    text: slides[0].subtitle || '',
    imageUrl: slides[0].imageUrl?._ || ''
  } : undefined;

  return (
    <>
      {backNavigation && <BackNavigation />}
      {shareNavigation && <ShareNavigation shareContent={shareContent} />}
      <SlideShow
        scrollContainerId='content'
        scrollSnapAnimation={slideScrollSnapAnimation}
        slideIds={slides.map(x => x.id)} zIndex={zIndices.base}
      >
        {slides?.map((slide: SlideContentProps, index: number) => (
          slide.detail?.title
            ?
            <SlideContentDetail
              key={slide.id}
              index={index}
              editor={isEditor}
              alwaysShowDownArrow={alwaysShowDownArrow}
              enabledDownArrow={shouldShowDownArrow(index === slides.length - 1)}
              slideScrollSnapAnimation={slideScrollSnapAnimation}
              {...slide}
              onLearnMoreClick={handleClick}
            />
            :
            <SlideContent
              key={slide.id}
              index={index}
              editor={isEditor}
              alwaysShowDownArrow={alwaysShowDownArrow}
              enabledDownArrow={shouldShowDownArrow(index === slides.length - 1)}
              slideScrollSnapAnimation={slideScrollSnapAnimation}
              {...slide}
            />
        ))}
        {(!isEditor && navigatorEnabled)
          &&
          <>
            <AppearDetector scrollContainerId='content' onIntersect={(isIntersect) => setNavigatorAppear(!isIntersect)} />
            <SectionNavigator zIndex={zIndices.sectionNavigator} navLinks={navLinks} appear={navigatorAppear} />
          </>
        }
        {/* -------WARNING ----- no dynamic height content in this container or it will mess up the animation, beware of images, need to have fixed height */}
        {/* TODO: last slide will be removed */}
        <Box
          id={'scroll-container-last-slide'}
          position='relative' zIndex={zIndices.lastSlide}
        >
          {
            [
              // remove empty
              ...(subcomponents || []).filter(Boolean),
              // leave at least one empty for adding component
              null
            ].map((o, i) => (
              <Slot
                key={i}
                editor={props.editor}
                component={o}
                componentIndex={i}
                parentId={props._id}
              />
            ))
          }
        </Box>
        {!editor && floatingCta.label && <FloatingCTA {...floatingCta} />}
      </SlideShow>
      <FullScreenModal
        open={!!activeModalId}
        onClose={() => setActiveModalId(null)}
      >
        {/* Swiper.js requires all props to be loaded first */}
        {slideHighlights?.highlights && (
          <Box ref={fullScreenModalRef}>
            <HighlightsDetail
              highlights={slideHighlights?.highlights}
              open={slideHighlights?.id === activeModalId}
              onClose={() => {
                setActiveModalId(null);
                // Mobile View - Scroll modal content to the top
                fullScreenModalRef?.current?.scrollIntoView();
              }}
            />
          </Box>
        )}
      </FullScreenModal>
    </>
  );
}

function Slot(props) {
  const { editor, parentId, component, componentIndex } = props;

  if (editor) {
    return (
      <EditorSlot editor={editor} parentId={parentId} component={component} componentIndex={componentIndex} />
    );
  }

  if (component) {
    const Component = ComponentLibrary[component.widgetId];

    // Render just the component
    return (
      <Component {...component.props} subcomponents={component.subcomponents} />
    );
  }

  return null;
}
